import type { ChangeEvent, FC } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Stack,
  TextField,
} from '@mui/material';

import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import OrgTreeItem from 'src/components/common/OrgTree/components/OrgTreeItem';
import useSearchResults from 'src/components/common/OrgTree/hooks/useSearchResults';

import OrgUnitList from './components/OrgUnitList';

import { styles } from './index.styles';

export interface OrgTreeSearchProps {
  itemsAreDraggable: boolean;
  onSelect: (moduleId: number) => void;
  onSearchStateChange?: (isSearching: boolean) => void;
  isSearching?: boolean;
}

export const ORG_TREE_SEARCH_TEST_ID = 'org-tree-search';

const OrgTreeSearch: FC<OrgTreeSearchProps> = ({
  itemsAreDraggable,
  onSelect,
  onSearchStateChange,
  isSearching,
}) => {
  const { t } = useTranslation(['report', 'common']);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchInput = useCallback(
    (event: ChangeEvent<any>) => {
      setSearchTerm(event.target.value);
      const isSearching = !!event.target.value;
      onSearchStateChange?.(isSearching);
    },
    [onSearchStateChange],
  );

  const { loading, orgUnits } = useSearchResults({
    searchTerm,
  });

  const handleSelect = useCallback(
    (orgUnit: ModuleOrPlanActionOrgUnit) => {
      onSelect(orgUnit.id);
      setSearchTerm('');
      onSearchStateChange?.(false);
    },
    [onSelect, onSearchStateChange],
  );
  // Using different label creation function because the orgUnitList used in the search overwrites the label references
  const createSearchLabel = useCallback(
    (orgUnit: ModuleOrPlanActionOrgUnit) => (
      <OrgTreeItem orgUnit={orgUnit} draggable={itemsAreDraggable} />
    ),
    [itemsAreDraggable],
  );

  return (
    <ClickAwayListener
      disableReactTree
      onClickAway={() => {
        onSearchStateChange?.(false);
      }}
    >
      <Stack
        direction="column"
        sx={styles.orgTreeSearch}
        data-test-id={ORG_TREE_SEARCH_TEST_ID}
      >
        <TextField
          fullWidth
          aria-label={t('sidebar.searchPlaceholder')}
          name="searchTerm"
          onChange={handleSearchInput}
          onFocus={(event: any) => {
            if (event.target.value) {
              onSearchStateChange?.(true);
            }
          }}
          placeholder={t('sidebar.searchPlaceholder')}
          value={searchTerm}
          hiddenLabel
          sx={{ mb: 1 }}
        />

        {isSearching && (
          <Box sx={styles.popup} data-test-id="popup">
            {orgUnits && orgUnits.length > 0 && (
              <OrgUnitList
                createLabel={createSearchLabel}
                onSelect={handleSelect}
                orgUnits={orgUnits}
              />
            )}

            {orgUnits && orgUnits.length === 0 && (
              <Box data-test-id="no-results">{t('common:noResults')}</Box>
            )}

            {loading && (
              <Stack
                direction="row"
                sx={{ justifyContent: 'center', ...styles.loadingIndicator }}
              >
                <CircularProgress size={20} />
              </Stack>
            )}
          </Box>
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export default OrgTreeSearch;
